import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Trash2, Plus, Check, X, ChevronsUpDown, UserPlus, Download, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useDuplicateGuard, DuplicateCustomerDialog } from "@/components/DuplicateCustomer";

export function ConfirmDelete({ onConfirm, title = "Naozaj zmazať?", description, children }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent data-testid="confirm-delete-dialog">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-destructive/15 sm:mx-0"><AlertTriangle className="h-6 w-6 text-destructive" /></div>
          <AlertDialogTitle className="font-heading uppercase">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description || "Túto akciu nie je možné vrátiť späť."}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-delete-cancel">Zrušiť</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-confirm">Zmazať</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function useKeyboardInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => { vv.removeEventListener("resize", onResize); vv.removeEventListener("scroll", onResize); };
  }, []);
  return inset;
}

export function CustomerCombobox({ customers, value, onChange, onAddNew, placeholder = "Vyberte zákazníka", testid = "customer-combobox" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const kbInset = useKeyboardInset();
  const selectedName = customers.find((c) => c.id === value)?.name || "";
  const filtered = customers.filter((c) => {
    const s = query.trim().toLowerCase();
    if (!s) return true;
    return [c.name, c.email, c.phone, c.ico].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const pick = (id) => { onChange(id); setOpen(false); setQuery(""); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-trigger`}
          className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring">
          <span className={value ? "truncate" : "truncate text-muted-foreground"}>{selectedName || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={4} collisionPadding={{ top: 12, bottom: kbInset + 12, left: 8, right: 8 }} className="w-[--radix-popover-trigger-width] overflow-hidden p-0" data-testid={`${testid}-dropdown`}>
        <div className="border-b border-border/50 p-2">
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Hľadať meno, IČO, telefón…" data-testid={`${testid}-search`} />
        </div>
        <div className="max-h-56 overflow-y-auto">
          <button type="button" onClick={() => pick("")} data-testid={`${testid}-none`} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-accent">
            <span className="text-muted-foreground">Bez zákazníka</span>{!value && <Check className="h-4 w-4 text-primary" />}
          </button>
          {filtered.map((c) => (
            <button key={c.id} type="button" onClick={() => pick(c.id)} data-testid={`${testid}-option-${c.id}`} className="flex w-full items-center justify-between gap-2 border-t border-border/40 px-3 py-2 text-left text-sm transition-colors hover:bg-accent">
              <span className="min-w-0"><span className="block truncate font-medium">{c.name}</span>{(c.phone || c.ico) && <span className="block text-xs text-muted-foreground">{[c.phone, c.ico && `IČO ${c.ico}`].filter(Boolean).join(" · ")}</span>}</span>
              {value === c.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </button>
          ))}
          {!filtered.length && <div className="px-3 py-3 text-sm text-muted-foreground">Žiadny zákazník</div>}
        </div>
        {onAddNew && (
          <button type="button" onClick={() => { const q = query.trim(); setOpen(false); onAddNew(q); }} data-testid={`${testid}-add-new`}
            className="flex w-full items-center gap-2 border-t border-border bg-background/60 px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors hover:bg-accent">
            <UserPlus className="h-4 w-4" /> Nový zákazník{query.trim() ? ` „${query.trim()}"` : ""}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const emptyCustomer = { name: "", is_company: true, ico: "", dic: "", ic_dph: "", address: "", city: "", psc: "", country: "SK", email: "", phone: "", note: "" };

export function QuickCustomerDialog({ open, onOpenChange, initialName = "", onCreated, onPickExisting }) {
  const [form, setForm] = useState(emptyCustomer);
  const { dup, setDup, guard } = useDuplicateGuard();
  const [lookupLoading, setLookupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setForm({ ...emptyCustomer, name: initialName || "" }); }, [open, initialName]);

  const lookup = async () => {
    setLookupLoading(true);
    try {
      const { data } = await api.get("/lookup/company", { params: { country: form.country, q: form.ico || form.name } });
      if (data.found) {
        setForm((f) => ({ ...f, name: data.name || f.name, ico: data.ico || f.ico, dic: data.dic || f.dic, ic_dph: data.ic_dph || f.ic_dph, address: data.address || f.address, city: data.city || f.city, psc: data.psc || f.psc, country: data.country || f.country }));
        toast.success("Údaje firmy doplnené");
      } else toast.warning(data.message || "Firma sa nenašla – doplňte ručne");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLookupLoading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Zadajte názov / meno"); return; }
    setSaving(true);
    try { await guard(form.name, "", doSave); } finally { setSaving(false); }
  };
  const doSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Zákazník vytvorený a priradený");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="quick-customer-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">Nový zákazník</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Krajina</Label>
              <Select value={form.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger data-testid="qc-country"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SK">SK (RPO)</SelectItem><SelectItem value="CZ">CZ (ARES)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>IČO</Label>
              <div className="flex gap-2">
                <Input value={form.ico} onChange={(e) => set("ico", e.target.value)} data-testid="qc-ico" />
                <Button type="button" variant="outline" className="gap-1 whitespace-nowrap" onClick={lookup} disabled={lookupLoading} data-testid="qc-lookup-button">
                  {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Nájsť firmu
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Názov / Meno</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="qc-name" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>DIČ</Label><Input value={form.dic} onChange={(e) => set("dic", e.target.value)} data-testid="qc-dic" /></div>
            <div className="space-y-1.5"><Label>IČ DPH</Label><Input value={form.ic_dph} onChange={(e) => set("ic_dph", e.target.value)} data-testid="qc-icdph" /></div>
          </div>
          <div className="space-y-1.5"><Label>Adresa</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="qc-address" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>PSČ</Label><Input value={form.psc} onChange={(e) => set("psc", e.target.value)} data-testid="qc-psc" /></div>
            <div className="space-y-1.5"><Label>Mesto</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="qc-city" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="qc-email" /></div>
            <div className="space-y-1.5"><Label>Telefón</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="qc-phone" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Zrušiť</Button>
          <Button onClick={save} disabled={saving} data-testid="qc-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vytvoriť a priradiť"}</Button>
        </DialogFooter>
        <DuplicateCustomerDialog dup={dup} setDup={setDup} openLabel="Použiť existujúceho" onOpenExisting={(c) => { onPickExisting?.(c); onOpenChange(false); toast.success("Priradený existujúci zákazník"); }} />
      </DialogContent>
    </Dialog>
  );
}

const emptyVehicle = { spz: "", country: "SK", vin: "", brand: "", model: "", year: "", km: "" };

export function QuickVehicleDialog({ open, onOpenChange, customerId, onCreated }) {
  const [qa, setQa] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const setF = (k, v) => setQa((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setQa(emptyVehicle); }, [open]);

  const lookupVin = async () => {
    if (!qa.spz) { toast.error("Zadajte ŠPZ"); return; }
    setLooking(true);
    try {
      const { data } = await api.get("/lookup/vehicle", { params: { spz: qa.spz, country: qa.country } });
      if (data.found) { setQa((f) => ({ ...f, vin: data.vin || f.vin, brand: data.brand || f.brand, model: data.model || f.model, year: data.year || f.year })); toast.success(data.cached ? "Doplnené z pamäte" : "Vozidlo doplnené"); if (data.credit_charged) toast.info(`−${data.credit_charged} ${data.credit_charged === 1 ? "kredit" : "kreditov"} · zostáva ${data.credits_left}`); }
      else toast.warning(data.message || "Nenájdené – doplňte ručne");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLooking(false); }
  };
  const save = async () => {
    if (!qa.spz.trim()) { toast.error("Zadajte ŠPZ"); return; }
    setSaving(true);
    try {
      const payload = { spz: qa.spz, vin: qa.vin, brand: qa.brand, model: qa.model, year: qa.year ? Number(qa.year) : null, km: qa.km !== "" ? Number(qa.km) : null, customer_id: customerId || null };
      const { data } = await api.post("/vehicles", payload);
      toast.success("Vozidlo pridané a priradené");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-vehicle-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Rýchle pridanie vozidla</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-1 rounded-lg border border-border bg-background/60 p-1">
            {["SK", "CZ"].map((cc) => (<button key={cc} type="button" onClick={() => setF("country", cc)} data-testid={`qv-country-${cc}`} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${qa.country === cc ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{cc}</button>))}
          </div>
          <div className="space-y-1.5"><Label>ŠPZ</Label><div className="flex gap-2"><Input value={qa.spz} onChange={(e) => setF("spz", e.target.value.toUpperCase())} placeholder="napr. 1AB2345" data-testid="qv-spz" /><Button type="button" variant="outline" className="whitespace-nowrap" onClick={lookupVin} disabled={looking} data-testid="qv-lookup">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Načítať VIN"}</Button></div></div>
          <div className="space-y-1.5"><Label>VIN</Label><Input value={qa.vin} onChange={(e) => setF("vin", e.target.value.toUpperCase())} className="font-mono" data-testid="qv-vin" /></div>
          <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label>Značka</Label><Input value={qa.brand} onChange={(e) => setF("brand", e.target.value)} data-testid="qv-brand" /></div><div className="space-y-1.5"><Label>Model</Label><Input value={qa.model} onChange={(e) => setF("model", e.target.value)} data-testid="qv-model" /></div></div>
          <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label>Rok</Label><Input type="number" value={qa.year} onChange={(e) => setF("year", e.target.value)} data-testid="qv-year" /></div><div className="space-y-1.5"><Label>Stav KM</Label><Input type="number" value={qa.km} onChange={(e) => setF("km", e.target.value)} data-testid="qv-km" /></div></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Zrušiť</Button><Button onClick={save} disabled={saving} data-testid="qv-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pridať a priradiť"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const STATUS = {
  naplanovane: { label: "Naplánované", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  rozpracovana: { label: "Rozpracovaná", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  neuhradena: { label: "Neuhradená", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  dokoncena: { label: "Uhradená", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.rozpracovana;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`status-badge-${status}`}>{s.label}</span>;
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-heading text-2xl font-extrabold uppercase tracking-wide sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function usePaginated(list, defaultSize = 10) {
  const [pageSize, setPageSize] = useState(defaultSize);
  const [page, setPage] = useState(1);
  const total = list.length;
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [pageSize, total]);
  const safePage = Math.min(page, totalPages);
  const paged = pageSize === "all" ? list : list.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { paged, page: safePage, setPage, pageSize, setPageSize, total, totalPages };
}

const PAGE_SIZES = [10, 20, 50, ["all", "Všetko"]];

export function Paginator({ page, setPage, pageSize, setPageSize, total, totalPages }) {
  const [jump, setJump] = useState("");
  if (!total) return null;
  const from = pageSize === "all" ? 1 : (page - 1) * pageSize + 1;
  const to = pageSize === "all" ? total : Math.min(total, page * pageSize);
  const go = () => { const n = parseInt(jump, 10); if (n >= 1 && n <= totalPages) setPage(n); setJump(""); };
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="paginator">
      <div className="text-sm text-muted-foreground" data-testid="paginator-info">
        Zobrazené <b className="text-foreground">{from}–{to}</b> z <b className="text-foreground">{total}</b>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
          {PAGE_SIZES.map((s) => {
            const val = Array.isArray(s) ? s[0] : s;
            const lbl = Array.isArray(s) ? s[1] : s;
            return (
              <button key={val} onClick={() => setPageSize(val)} data-testid={`page-size-${val}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${pageSize === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
            );
          })}
        </div>
        {pageSize !== "all" && totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /> Späť</Button>
            <span className="text-sm text-muted-foreground" data-testid="page-indicator">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" className="gap-1" disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))} data-testid="page-next">Ďalej <ChevronRight className="h-4 w-4" /></Button>
            {totalPages > 5 && (
              <div className="flex items-center gap-1.5 pl-1">
                <span className="text-sm text-muted-foreground">Strana</span>
                <Input type="number" min={1} max={totalPages} value={jump} onChange={(e) => setJump(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") go(); }} placeholder={String(page)}
                  className="h-9 w-16 text-center" data-testid="page-jump-input" />
                <Button variant="secondary" size="sm" onClick={go} disabled={!jump} data-testid="page-jump-go">Prejsť</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const BASE_UNITS = ["ks", "h", "g", "kg", "l", "km", "m", "sada", "bal"];
const NONE_UNIT = "__none__";

export function defaultVat(vatMode) {
  return vatMode === "RC" ? 0 : vatMode === "CZ" ? 21 : 23;
}

const _normTxt = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
export function inspectionPhrases(val) {
  const n = _normTxt(val);
  if (n.length < 2) return [];
  const out = [];
  if (/\btk\b/.test(n) || n.includes("tech") || n.includes("kontrol")) out.push("Technická kontrola (TK)");
  if (/\bek\b/.test(n) || n.includes("emis") || n.includes("kontrol")) out.push("Emisná kontrola (EK)");
  if (n.includes("sprostred") || /\btk\b/.test(n) || /\bek\b/.test(n)) out.push("Sprostredkovanie TK + EK");
  return [...new Set(out)];
}

export function ItemsEditor({ items, onChange, vatMode = "SK" }) {
  const [customUnits, setCustomUnits] = useState([]);
  const [hiddenUnits, setHiddenUnits] = useState([]);
  const [newUnit, setNewUnit] = useState("");
  const [savingUnit, setSavingUnit] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setCustomUnits(Array.isArray(r.data?.units) ? r.data.units : []);
      setHiddenUnits(Array.isArray(r.data?.units_hidden) ? r.data.units_hidden : []);
    }).catch(() => {});
  }, []);

  const displayedUnits = [
    ...BASE_UNITS.filter((u) => !hiddenUnits.includes(u)),
    ...customUnits.filter((u) => !BASE_UNITS.includes(u)),
  ];

  const saveUnit = async () => {
    const u = newUnit.trim();
    if (!u) return;
    try {
      if (BASE_UNITS.includes(u)) {
        const nextHidden = hiddenUnits.filter((x) => x !== u);
        await api.put("/settings", { units_hidden: nextHidden });
        setHiddenUnits(nextHidden);
      } else if (customUnits.includes(u)) {
        toast.warning("Táto jednotka už existuje"); return;
      } else {
        const next = [...customUnits, u];
        setSavingUnit(true);
        await api.put("/settings", { units: next });
        setCustomUnits(next);
      }
      setNewUnit("");
      toast.success(`Jednotka „${u}" pridaná`);
    } catch { toast.error("Skratku sa nepodarilo uložiť"); }
    finally { setSavingUnit(false); }
  };

  const deleteUnit = async (u) => {
    try {
      if (BASE_UNITS.includes(u)) {
        const nextHidden = [...hiddenUnits, u];
        await api.put("/settings", { units_hidden: nextHidden });
        setHiddenUnits(nextHidden);
      } else {
        const next = customUnits.filter((x) => x !== u);
        await api.put("/settings", { units: next });
        setCustomUnits(next);
      }
      toast.success(`Jednotka „${u}" odstránená`);
    } catch { toast.error("Skratku sa nepodarilo odstrániť"); }
  };

  const update = (i, field, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const add = () => onChange([...items, { description: "", kind: "praca", qty: 1, unit: "", unit_price: "", discount: "", vat_rate: defaultVat(vatMode) }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  let subtotal = 0, vat = 0;
  items.forEach((it) => {
    const line = Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100);
    subtotal += line;
    vat += vatMode === "RC" ? 0 : line * Number(it.vat_rate || 0) / 100;
  });

  return (
    <div className="space-y-3">
      {items.length > 0 && (
        <div className="hidden grid-cols-12 gap-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
          <div className="col-span-4">Popis položky</div>
          <div className="col-span-1">Množ.</div>
          <div className="col-span-2">Jednotka</div>
          <div className="col-span-1">Cena € (bez DPH)</div>
          <div className="col-span-1">Zľava %</div>
          <div className="col-span-1">DPH %</div>
          <div className="col-span-1 text-right">Spolu</div>
          <div className="col-span-1"></div>
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 rounded-lg border border-border bg-background/40 p-2" data-testid={`item-row-${i}`}>
          <div className="relative col-span-12 sm:col-span-4">
            <Input className="w-full" placeholder="Popis položky" value={it.description} autoComplete="off"
              onChange={(e) => { update(i, "description", e.target.value); setSuggestIdx(i); }}
              onFocus={() => setSuggestIdx(i)}
              onBlur={() => setTimeout(() => setSuggestIdx((c) => (c === i ? null : c)), 150)}
              data-testid={`item-desc-${i}`} />
            {suggestIdx === i && inspectionPhrases(it.description).length > 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid={`item-suggest-${i}`}>
                {inspectionPhrases(it.description).map((p, k) => (
                  <button key={k} type="button" onMouseDown={(e) => { e.preventDefault(); update(i, "description", p); setSuggestIdx(null); }}
                    className="block w-full border-b border-border/50 px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-accent" data-testid={`item-suggest-${i}-${k}`}>{p}</button>
                ))}
              </div>
            )}
          </div>
          <Input type="number" step="0.01" className="col-span-3 sm:col-span-1" placeholder="Množ." value={it.qty} onFocus={(e) => e.target.select()} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
          <div className="col-span-3 sm:col-span-2">
            <Select value={it.unit || undefined} onValueChange={(v) => update(i, "unit", v === NONE_UNIT ? "" : v)}>
              <SelectTrigger data-testid={`item-unit-${i}`}><SelectValue placeholder="j." /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_UNIT} data-testid="unit-option-none"><span className="text-muted-foreground">— bez jednotky</span></SelectItem>
                {displayedUnits.map((u) => (
                  <SelectItem key={u} value={u} className="pr-8" data-testid={`unit-option-${u}`}>
                    {u}
                    <button type="button" aria-label={`Odstrániť ${u}`} data-testid={`unit-delete-${u}`}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); deleteUnit(u); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </SelectItem>
                ))}
                <div className="mt-1 border-t border-border px-1 pt-2" onPointerDown={(e) => e.stopPropagation()}>
                  <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Pridať skratku</p>
                  <div className="flex items-center gap-1 p-1">
                    <Input className="h-8" placeholder="napr. l, deň, sada" value={newUnit}
                      onChange={(e) => setNewUnit(e.target.value)}
                      onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); saveUnit(); } }}
                      data-testid="new-unit-input" />
                    <Button type="button" size="icon" className="h-8 w-8 shrink-0" onClick={saveUnit} disabled={savingUnit || !newUnit.trim()} data-testid="save-unit-button"><Check className="h-4 w-4" /></Button>
                  </div>
                </div>
              </SelectContent>
            </Select>
          </div>
          <Input type="number" step="0.01" className="col-span-3 sm:col-span-1" placeholder="Cena €" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
          <Input type="number" step="1" className="col-span-3 sm:col-span-1" placeholder="Zľava %" value={it.discount} onChange={(e) => update(i, "discount", e.target.value)} data-testid={`item-discount-${i}`} />
          <Input type="number" step="1" className="col-span-3 sm:col-span-1" placeholder="DPH %" value={it.vat_rate} onChange={(e) => update(i, "vat_rate", e.target.value)} disabled={vatMode === "RC"} data-testid={`item-vat-${i}`} />
          <div className="col-span-6 sm:col-span-1 flex items-center justify-end font-mono text-sm">{eur(Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100))}</div>
          <div className="col-span-6 sm:col-span-1 flex items-center justify-end">
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`item-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={add} className="gap-2" data-testid="add-item-button"><Plus className="h-4 w-4" /> Pridať položku</Button>
      </div>
      <div className="flex flex-col items-end gap-1 border-t border-border pt-3 text-sm">
        <div className="flex w-full max-w-xs justify-between"><span className="text-muted-foreground">Základ (bez DPH)</span><span className="font-mono">{eur(subtotal)}</span></div>
        <div className="flex w-full max-w-xs justify-between"><span className="text-muted-foreground">DPH</span><span className="font-mono">{eur(vat)}</span></div>
        <div className="flex w-full max-w-xs justify-between text-base font-bold"><span>Spolu (s DPH)</span><span className="font-mono text-primary">{eur(subtotal + vat)}</span></div>
      </div>
    </div>
  );
}
